import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { toDataURL } from 'qrcode';
import { saveAs } from 'file-saver';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { ERROR_PROFILE_PIC_LOADING, QR_CODE_SUFFIX } from 'src/app/global';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { PATH_PATIENTS, PATH_EDIT_PATIENT } from '../../patients.global';
import { getStorage, getDownloadURL, ref } from 'firebase/storage';
import { PatientDashboardStore } from '../../state/dashboard-page.store';
import { map, takeUntil } from 'rxjs/operators';

//To Download Barcodes
import * as JsBarcode from 'jsbarcode';
import { jsPDF } from 'jspdf';
import { BARCODE_SUFFIX } from 'src/app/global';
import { PanelModule } from 'primeng/panel';
import { NgIf, NgFor } from '@angular/common';
import { MenuModule } from 'primeng/menu';
import { Button } from 'primeng/button';

@Component({
  selector: 'app-patient-snapshot',
  templateUrl: './patient-snapshot.component.html',
  styleUrls: ['./patient-snapshot.component.scss'],
  standalone: true,
  imports: [
    PanelModule,
    PrimeTemplate,
    MenuModule,
    Button,
  ],
})
export class PatientSnapshotComponent implements OnInit {
  patient: Patient = new Patient();
  profilePicSrc = '';

  menuItems: MenuItem[] = [
    {
      label: 'Edit Patient Info',
      icon: 'pi pi-pencil',
      command: () => this.navigateToEditPatientPage(),
    },
    {
      label: 'Download QR Code',
      icon: 'pi pi-qrcode',
      command: () => this.downloadQR(),
    },
    {
      label: 'Generate Patient Wristband',
      icon: 'pi pi-file-pdf',
      command: () => this.generateWristbandWithBarcode(this.patient),
    },
  ];

  constructor(private readonly patientStore: PatientDashboardStore, private router: Router) {
    this.patientStore.patient$
      .pipe(
        takeUntil(this.patientStore.destroy$),
        map((patient, index) => ({ patient, index }))
      )
      .subscribe(({ patient, index }) => {
        this.patient = patient;

        this.getProfilePicture(this.patient.id);

        // VTN2: Why was this here?
        // if (index === 0) {
        //   this.getProfilePicture(this.patient.id);
        // }
      });
  }

  ngOnInit(): void { }

  /**
   * Retrieves the patient's profile picture URL from Firebase Storage.
   */
  getProfilePicture(ID: string) {
    const id = ID.includes('-') ? ID.split('-')[1] : ID;

    // Get profile picture
    const storage = getStorage();
    const path = `${id}/${this.patient.profilePicName}`;

    const storageRef = ref(storage, path);

    getDownloadURL(storageRef)
      .then((url) => {
        this.profilePicSrc = url;
      })
      .catch((error) => {
        console.log(ERROR_PROFILE_PIC_LOADING);
      });
  }

  /**
   * Navigates user to the Edit Patient page.
   */
  navigateToEditPatientPage() {
    this.router.navigate([PATH_PATIENTS, PATH_EDIT_PATIENT, this.patient.id]);
  }

  /**
   * Downloads the current patient's MRN as a QR code image.
   */
  downloadQR() {
    toDataURL(this.patient.getId(), (err, url) => {
      saveAs(url, this.patient.getId() + QR_CODE_SUFFIX);
    });
  }

  /*
    Generate Patient wristband w/ name, QR code/barcode, and MRN
  */

  /**
   * Creates a hidden HTMLCanvasElement that contains the barcode image.
   *
   * @param patient
   * @returns HTMLCanvasElement of the generated barcode
   *
   */
  generateHTMLCanvasElement(patient: Patient): HTMLCanvasElement {
    let canvas = document.getElementById('barcode') as HTMLCanvasElement;

    if (!canvas) {
      // If the canvas doesn't exist, create it dynamically
      canvas = document.createElement('canvas');
      canvas.id = 'barcode';
      //document.body.appendChild(canvas); // Optionally append it to the DOM
      console.warn('Barcode canvas was missing, so a new one was created');
    }

    // Generate the barcode
    JsBarcode(canvas, patient.getMRN(), {
      width: 2.5,
      height: 100,
    });

    return canvas;
    // Reference: https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob
    //return document.getElementById('barcode') as HTMLCanvasElement;
  }

  /**
   * Saves a PNG of the specified medication's barcode to the user's Downloads folder.
   *
   * @param patient Medication to download barcode for
   */
  public downloadBarcode(patient: Patient) {
    const barcode = this.generateHTMLCanvasElement(patient);
    if (barcode) {
      barcode.toBlob((blob) => {
        if (blob) {
          saveAs(blob, patient.getMRN() + BARCODE_SUFFIX);
        } else {
          console.error('Failed to create blob for barcode download');
        }
      });
    } else {
      console.error('Barcode generation failed');
    }
  }

  /**
   * Downloads barcodes for all selected medications into a PDF.
   * Reference: https://github.com/parallax/jsPDF.
   */

  public generateWristbandWithBarcode(patient: Patient) {
    const pdf = new jsPDF('landscape');
    const pageHeight = pdf.internal.pageSize.height;
    const pageWidth = pdf.internal.pageSize.width;

    const wristBandHeight = 35;
    const wristBandWidth = pageWidth - 8 * 15;

    const x = 10;
    let y = 20;

    //Main Rectangle for Information
    pdf.setDrawColor(0);
    pdf.setLineWidth(1);
    pdf.rect(x, y, wristBandWidth, wristBandHeight);

    //Rectangle for wrist band w/ holes
    pdf.rect(x + wristBandWidth, wristBandHeight - 7, 100, 18);
    let holex = x + wristBandWidth;
    for (let i = 0; i < 7; i++) {
      holex += 13;
      pdf.circle(holex, wristBandHeight + 2.5, 3, 'F');
    }

    //Top line for 'Calvin Hospital' & Patient Name
    y += 15;
    pdf.setFontSize(16);
    pdf.text('Calvin Hospital', x + 3, y).setFont('italic');
    pdf
      .text(patient.getNameFirstMiddleLast(), x + 46, y)
      .setFontSize(16)
      .setFont('bold');

    toDataURL(patient.getId(), { errorCorrectionLevel: 'H' }, (err, url) => {
      if (err) {
        console.error('Error generating QR code:', err);
        return;
      }

      // QR also in top line
      const qrCodeWidth = 16;
      const qrCodeHeight = 16;
      pdf.addImage(url, 'PNG', x + 90, y - 13, qrCodeWidth, qrCodeHeight);

      //Second line for DOB, MRN & allergies
      y += 15;
      pdf.setFontSize(16);
      pdf.setFont('bold');
      const dobString = patient.getDOBString().split('/').slice(0, 2).join('/');
      //DOB x coordinate
      const dobX = x + 3;
      pdf.text(dobString, dobX, y).setFont('Bold');

      //Age x coordinate
      const ageX = x + 20;
      pdf.text(patient.getAge() + ' YO', ageX, y);

      //Allergies x coordinate
      const allergiesX = x + 49;
      const allergies = patient.getAllergies() || 'None';
      pdf.text('Allergies: ' + allergies, allergiesX, y).setFont('Bold');

      //MRN x coordinate
      const mrnX = x + 105;
      pdf.text('MRN: ' + patient.getMRN(), mrnX, y).setFont('Bold');

      pdf.save(patient.getMRN() + '_wristband.pdf');
    });
  }
}
